// =============================================================================
// features/assistant/blocks/LeadListCard.tsx — Card do bloco `stage_leads`
// (F30-S02/S03/S04): lista de leads de um estágio do funil, com diagnóstico
// calculado por query (dias parado, contato, simulação/análise) e ações de
// funil por lead (LeadActionButtons).
//
// LGPD (doc 17 §8.1/§8.3): `leads[].name` É PII — exibida aqui porque o
// usuário já tem o RBAC necessário verificado no backend antes deste bloco
// existir (mesmo padrão de lead_summary/lead-search). Nunca logar, nunca
// persistir fora do ciclo de vida do componente.
// =============================================================================

import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';

import { BlockCardShell } from './BlockCardShell';
import { BlockCardUnavailable } from './BlockCardUnavailable';
import { formatDateBR, formatTimeBR } from './format';
import { isStageLeadsValue } from './guards';
import { UsersIcon } from './icons';
import { LeadActionButtons } from './LeadActionButtons';
import type { StageLeadItem } from './types';

interface LeadListCardProps {
  value: unknown;
}

/** Rótulos PT-BR do papel canônico do estágio (kanban_stages.canonical_role,
 * KANBAN_CANONICAL_ROLES em apps/api/src/db/schema/kanbanStages.ts). Papel
 * desconhecido (forward-compat) cai no valor bruto — nunca quebra o render. */
const CANONICAL_ROLE_LABELS: Record<string, string> = {
  pre_atendimento: 'Pré-atendimento',
  simulacao: 'Simulação',
  documentacao: 'Documentação',
  analise_credito: 'Análise de crédito',
  concluido_ganho: 'Concluído (ganho)',
  concluido_perdido: 'Concluído (perdido)',
};

export function labelForCanonicalRole(role: string): string {
  return CANONICAL_ROLE_LABELS[role] ?? role;
}

export function LeadListCard({ value }: LeadListCardProps): React.JSX.Element {
  if (!isStageLeadsValue(value)) {
    return (
      <BlockCardShell
        icon={<UsersIcon className="w-5 h-5" />}
        title="Leads do estágio"
        variant="neutral"
      >
        <BlockCardUnavailable />
      </BlockCardShell>
    );
  }

  const { leads, canonical_role, truncated } = value;
  const stageLabel = labelForCanonicalRole(canonical_role);
  const badge = `${leads.length}${truncated ? '+' : ''} lead${leads.length === 1 ? '' : 's'}`;

  return (
    <BlockCardShell
      icon={<UsersIcon className="w-5 h-5" />}
      title={`Leads em ${stageLabel}`}
      variant="neutral"
      badge={badge}
    >
      {leads.length === 0 ? (
        <p className="font-sans text-xs text-ink-4 italic">Nenhum lead neste estágio.</p>
      ) : (
        <div className="flex flex-col gap-2 max-h-[28rem] overflow-y-auto pr-1">
          {leads.map((lead) => (
            <LeadListItem key={lead.lead_id} lead={lead} />
          ))}
        </div>
      )}
    </BlockCardShell>
  );
}

function LeadListItem({ lead }: { lead: StageLeadItem }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-sm border border-border-subtle bg-surface-2 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-sans font-semibold text-sm text-ink truncate">{lead.name}</p>
          <p className="font-sans text-xs text-ink-3 mt-0.5 truncate">
            {lead.city_name ?? 'Sem cidade'} · {lead.assignee_name ?? 'Sem responsável'}
          </p>
        </div>
        <span className="font-mono text-xs text-ink-4 shrink-0 whitespace-nowrap">
          {lead.days_in_stage}d parado
        </span>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant={lead.has_simulation ? 'success' : 'neutral'}>
          {lead.has_simulation ? 'Com simulação' : 'Sem simulação'}
        </Badge>
        <Badge variant={lead.has_analysis ? 'success' : 'neutral'}>
          {lead.has_analysis ? 'Com análise' : 'Sem análise'}
        </Badge>
        {lead.last_contact_at && (
          <span className="font-mono text-xs text-ink-4">
            Último contato: {formatDateBR(lead.last_contact_at)}{' '}
            {formatTimeBR(lead.last_contact_at)}
          </span>
        )}
      </div>

      <LeadActionButtons lead={lead} />
    </div>
  );
}
